import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { FaqCategory } from "@/types/faq";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Null = create. Non-null = edit, and the key becomes read-only. */
  category: FaqCategory | null;
  isSaving: boolean;
  onSubmit: (values: {
    key: string;
    sectionNumber: number;
    sectionTitle: { ar: string; en: string };
    title: { ar: string; en: string };
    shortCode: string | null;
    sortOrder: number;
  }) => void;
}

const EMPTY = { ar: "", en: "" };

/**
 * Add or edit one FAQ category (a panel on the public page).
 *
 * 🔑 `sectionNumber` IS THE GROUPING, and it is worth understanding before editing it: categories
 * sharing a number are drawn as one expandable group on the page with the section title above
 * them, and a number held by exactly ONE category is drawn as a single direct link with no
 * section title. So moving a category into another section can change how BOTH sections render.
 *
 * 🔴 The key is read-only when editing — it is the panel's anchor (/Open-Budget-Survey-questionnaire#s1-pbs) and the join its
 * questions resolve through, and the backend refuses to change it.
 */
export function FaqCategoryDialog({ open, onOpenChange, category, isSaving, onSubmit }: Props) {
  const isEdit = category !== null;
  const [key, setKey] = useState("");
  const [sectionNumber, setSectionNumber] = useState(1);
  const [sectionTitle, setSectionTitle] = useState(EMPTY);
  const [title, setTitle] = useState(EMPTY);
  const [shortCode, setShortCode] = useState("");
  const [sortOrder, setSortOrder] = useState(0);

  useEffect(() => {
    if (!open) return;
    setKey(category?.key ?? "");
    setSectionNumber(category?.sectionNumber ?? 1);
    setSectionTitle(category?.sectionTitle ?? EMPTY);
    setTitle(category?.title ?? EMPTY);
    setShortCode(category?.shortCode ?? "");
    setSortOrder(category?.sortOrder ?? 0);
  }, [open, category]);

  const canSave =
    key.trim().length > 0 && (title.ar.trim().length > 0 || title.en.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "تعديل القسم" : "إضافة قسم"}</DialogTitle>
          <DialogDescription>
            الأقسام التي تحمل الرقم نفسه تُعرض مجمّعة تحت عنوان القسم. لن يظهر
            القسم على الصفحة العربية ما لم يكن له عنوان عربي.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="grid gap-2">
              <Label htmlFor="cat-key">المعرّف</Label>
              <Input
                id="cat-key"
                value={key}
                dir="ltr"
                disabled={isEdit}
                placeholder="s1-pbs"
                onChange={(e) => setKey(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cat-section">رقم القسم</Label>
              <Input
                id="cat-section"
                type="number"
                min={1}
                value={sectionNumber}
                onChange={(e) => setSectionNumber(Number(e.target.value) || 1)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cat-short">الاختصار</Label>
              <Input
                id="cat-short"
                value={shortCode}
                dir="ltr"
                placeholder="PBS"
                onChange={(e) => setShortCode(e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="cat-title-ar">عنوان القسم (عربي)</Label>
              <Input
                id="cat-title-ar"
                value={title.ar}
                onChange={(e) => setTitle({ ...title, ar: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cat-title-en">عنوان القسم (إنجليزي)</Label>
              <Input
                id="cat-title-en"
                value={title.en}
                dir="ltr"
                onChange={(e) => setTitle({ ...title, en: e.target.value })}
              />
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="cat-sec-ar">عنوان المجموعة (عربي)</Label>
              <Input
                id="cat-sec-ar"
                value={sectionTitle.ar}
                onChange={(e) => setSectionTitle({ ...sectionTitle, ar: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cat-sec-en">عنوان المجموعة (إنجليزي)</Label>
              <Input
                id="cat-sec-en"
                value={sectionTitle.en}
                dir="ltr"
                onChange={(e) => setSectionTitle({ ...sectionTitle, en: e.target.value })}
              />
            </div>
          </div>

          <div className="grid gap-2 sm:max-w-[12rem]">
            <Label htmlFor="cat-order">ترتيب العرض</Label>
            <Input
              id="cat-order"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            إلغاء
          </Button>
          <Button
            disabled={!canSave || isSaving}
            onClick={() =>
              onSubmit({
                key: key.trim().toLowerCase(),
                sectionNumber,
                sectionTitle,
                title,
                shortCode: shortCode.trim() || null,
                sortOrder,
              })
            }
          >
            {isSaving ? "جارٍ الحفظ…" : "حفظ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
